import { useState } from 'react';
import { Row, Col, Space, message, Upload } from 'antd';
import { ReModal, ReButton } from "@/components/refineElement";
import { getLangList } from '@/lib/i18n';
import { exportExcel } from "@/lib/exlsDownload";
import { excelUploadProps } from "@/lib/uploadFunction";
import { getSubjectColumnsByOrder, getFormatData } from "@/lib/transcript";
import { hasPermission } from "@/lib/permissionUtil";
import TranscriptConfig from "@/config/transcript";
import { ClassSubjectListType, ClassType } from "@/containers/organization/classManagement/types";
import { StudentType } from "@/containers/humanResourceManagement/studentManagement/types";
import { TranscriptListType } from './types';
import StudentTable from './StudentTable';
import ModalBatchScoring from './ModalBatchScoring';

const { updateTranscript, importTranscriptFile } = TranscriptConfig;

type Props = {
  studentArray: StudentType[];
  transcriptList: TranscriptListType;
  examId: number;
  getTranscriptListFunction: () => void;
  excelTitleWithClass: string;
  subjectInfo: ClassSubjectListType;
  classInfo: ClassType;
  year: string;
}

function Students(props: Props) {
  const {
    studentArray,
    transcriptList,
    examId,
    getTranscriptListFunction,
    excelTitleWithClass,
    subjectInfo,
    classInfo,
    year,
  } = props;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const { template } = classInfo;
  const subjectListForExam = subjectInfo[examId];
  const transcriptT: any = getLangList("transcript");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const errorCodeT: any = getLangList("errorCode");

  const clickBatchScoring = (e: any) => {
    e.stopPropagation();
    toggleModal('updateTranscript');
  };

  const clickExport = () => {
    const rowData: string[][] = [];
    const dynamicColumns = getSubjectColumnsByOrder(template, subjectListForExam);
    const allSubjectIdForClass = dynamicColumns.map((item) => item.dataIndex);
    const allSubjectNameForClass = dynamicColumns.map((item) => item.title);
    const excelHeader = ['cramSeat', 'username', 'nameCn', 'nameEn', ...allSubjectNameForClass];

    getFormatData(studentArray, transcriptList).forEach((student) => {
      const tempRowData = [];

      tempRowData.push(student.cramSeat, student.username, student.nameCn, student.nameEn);
      allSubjectIdForClass.forEach((subjectId) => {
        tempRowData.push((student as any)[subjectId]);
      });
      rowData.push(tempRowData);
    });

    exportExcel({
      data: [excelHeader, ...rowData],
      workbookName: excelTitleWithClass,
    });
  };

  function updateTranscriptFunction(values: { [subjectId: string]: string }) {
    const students = [];
    const specificSubjectId = Object.keys(values)?.[0]?.split('-')?.[1] ?? '0';
    const editingStudents = Object.keys(values).map((item) => {
      const [studentId, subjectId] = item.split('-');
      return { studentId, subjectId, subjectScore: values[item]?.toUpperCase() };
    })

    Object.entries(transcriptList).forEach(([studentId, value]) => {
      Object.entries(value).forEach(([subjectId, subjectScore]) => {
        subjectId !== specificSubjectId && students.push({ studentId, subjectId, subjectScore });
      });
    });

    students.push(...editingStudents);

    if (!hasPermission('TRANSCRIPT_MANAGEMENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateTranscript({ examId, students }).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getTranscriptListFunction();
    });
  };

  function toggleModal(handleType: string = '') {
    setIsModalVisible(!isModalVisible);
    setHandleType(handleType);
  };

  function clickImport(e: any) {
    const { file, onSuccess, onError } = e;
    const params = { fileFormat: { file }, stringFormat: { examId } };
    const isTooLarge = file.size / 1024 / 1024 > 50;

    if (isTooLarge) {
      message.error(errorT.IMAGE_TOO_LARGE);
      return onError();
    }

    importTranscriptFile(params).then((res: any) => {
      if (!res.success) {
        message.error(errorCodeT[res.errorCode] ?? errorT.failed);
        return onError();
      }
      onSuccess();
      message.success(errorT.success);
      getTranscriptListFunction();
    });
  };

  return (
    <>
      <Row gutter={[0, 12]} justify="end">
        <Col>
          <Space>
            {hasPermission('TRANSCRIPT_MANAGEMENT_EDIT') &&
              <ReButton iconName='edit' onClick={clickBatchScoring}>
                {transcriptT.BATCH_SCORING}
              </ReButton>
            }

            {hasPermission('TRANSCRIPT_MANAGEMENT_EDIT') &&
              <Upload {...excelUploadProps} customRequest={clickImport}>
                <ReButton iconName='import'>
                  {buttonT.IMPORT}
                </ReButton>
              </Upload>
            }

            <ReButton iconName='export' onClick={clickExport}>
              {buttonT.EXPORT}
            </ReButton>
          </Space>
        </Col>

        <Col span={24}>
          <StudentTable
            studentArray={studentArray}
            transcriptList={transcriptList}
            examId={examId}
            getTranscriptListFunction={getTranscriptListFunction}
            subjectInfo={subjectInfo}
            classInfo={classInfo}
            year={year}
          />
        </Col>
      </Row>

      {isModalVisible && handleType === 'updateTranscript' &&
        <ReModal
          title={transcriptT.BATCH_SCORING}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={updateTranscriptFunction}
          CollectedForm={ModalBatchScoring}
          formProps={{
            transcriptList,
            studentArray,
            template,
            subjectListForExam,
          }}
          width={'900px'}
        />
      }
    </>
  )
}

export default Students;